import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Wrapper } from './style';

// Standalone configuration constants that can be used or overridden
export const DEFAULT_CHART_WIDTH = 900;
export const DEFAULT_CHART_HEIGHT = 500; // Updated default standalone height

const RadialBarChart = ({
  items,
  width = DEFAULT_CHART_WIDTH,
  height = DEFAULT_CHART_HEIGHT,
  id = '',
  // Configurable vertical distance BETWEEN adjacent red (top-pole) labels.
  redLabelTopVerticalGap = 12,
  // Configurable horizontal length for red labels to prevent overlap.
  redLabelHorizontalLength = 45,
  // Configurable vertical distance BETWEEN adjacent blue (bottom-pole) labels.
  blueLabelBottomVerticalGap = 12,
  // Configurable horizontal length for blue labels to prevent overlap.
  blueLabelHorizontalLength = 45,
  // Configurable shift for the chart group (default: center-center)
  chartOffsetX = null,
  chartOffsetY = null,
}) => {
  const ref = useRef();
  const ht = items?.length > 25 ? 500 : height;

  useEffect(() => {
    // clear
    d3.select(ref.current).selectAll('*').remove();
    if (!items || items.length === 0) return;

    const sorted = [...items].sort((a, b) => a.value - b.value);
    const data = [{ name: "", value: 0, isDummy: true }, ...sorted]
      .map((d, i) => ({ ...d, key: String(i) }));

    const svg = d3.select(ref.current)
      .attr('width', width)
      .attr('height', ht)
      .style('overflow', 'visible');

    // ---- Master G tag inside SVG with horizontal and vertical shift constants ----
    const offsetX = chartOffsetX !== null ? chartOffsetX : width / 2;
    const offsetY = chartOffsetY !== null ? chartOffsetY : ht / 2;

    const masterG = svg.append('g')
      .attr('class', 'chart-master-container');

    const innerRadius = 70;
    const outerRadius = Math.min(width, ht) / 2 - 55;
    const g = masterG.append('g')
      .attr('transform', `translate(${offsetX},${offsetY})`);

    const roundUpToNearestTen = num => Math.ceil(num / 10) * 10;
    const maxValue = roundUpToNearestTen(d3.max(items, d => d.value));
    const numCircles = maxValue <= 5 ? maxValue : 5;
    const step = maxValue / numCircles;
    const gridValues = d3.range(0, numCircles + 1).map(i => Math.round(i * step));

    const x = d3.scaleBand()
      .domain(data.map(d => d.key))
      .range([0, 2 * Math.PI])
      .align(0);

    const y = d3.scaleLinear()
      .domain([0, maxValue])
      .range([innerRadius, outerRadius]);

    const computeValue = n => {
      if (n < 1) return 0;
      const v = 180 / (n + 1);
      return -(Math.round(v));
    };
    const rotationOffset = computeValue(items.length) * (Math.PI / 180);

    // grid circles
    g.selectAll('.grid-circle')
      .data(gridValues)
      .enter()
      .append('circle')
      .attr('class', 'grid-circle')
      .style('fill', 'none')
      .attr('r', d => y(d))
      .attr('stroke', '#D8D8D6')
      .attr('strokeWidth', '1');

    const arcGen = d3.arc()
      .innerRadius(innerRadius)
      .padAngle(0.03)
      .padRadius(innerRadius)
      .padRadius(outerRadius);

    // bars
    g.append('g')
      .selectAll('path')
      .data(data.filter(d => !d.isDummy))
      .enter()
      .append('path')
      .attr('fill', '#095BBD')
      .attr('d', d => arcGen({
        outerRadius: y(d.value),
        startAngle: x(d.key) + rotationOffset,
        endAngle: x(d.key) + x.bandwidth() + rotationOffset
      }))
      .on('mouseover', function(event, d) {
        d3.select(this).transition().duration(200).attr('fill', '#007bff')
          .attr('d', arcGen({
            outerRadius: y(d.value) + 5,
            startAngle: x(d.key) + rotationOffset,
            endAngle: x(d.key) + x.bandwidth() + rotationOffset
          }));
      })
      .on('mouseout', function(event, d) {
        d3.select(this).transition().duration(200).attr('fill', '#0056b3')
          .attr('d', arcGen({
            outerRadius: y(d.value),
            startAngle: x(d.key) + rotationOffset,
            endAngle: x(d.key) + x.bandwidth() + rotationOffset
          }));
      });

    // logic based on length
    const doHorizontal = items.length > 20;
    const smallFont = items.length > 30;
    const fontSize = smallFont ? '11px' : '12px';
    const gap = 5;

    const labelG = g.append('g')
      .selectAll('g')
      .data(data.filter(d => !d.isDummy))
      .enter()
      .append('g');

    // ---- Precompute red (top pole) label offsets + edge roles ----
    const lineEnd = outerRadius + 20;
    const redInfo = data
      .filter(d => !d.isDummy)
      .map(d => {
        const angle = (x(d.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
        const sinA = Math.sin(angle);
        const sortKey = x(d.key) + rotationOffset;
        return { key: d.key, sinA, sortKey };
      })
      .filter(r => r.sinA < -0.85)
      .sort((a, b) => a.sortKey - b.sortKey);

    const redCenterIdx = (redInfo.length - 1) / 2;
    const redOffsetMap = {};
    const redRoleMap = {};
    redInfo.forEach((r, idx) => {
      const distFromCenter = Math.abs(idx - redCenterIdx);
      redOffsetMap[r.key] = -distFromCenter * redLabelTopVerticalGap;

      if (idx === 0) redRoleMap[r.key] = 'rightmost';
      if (idx === redInfo.length - 1) redRoleMap[r.key] = 'leftmost';
    });

    // ---- Precompute blue (bottom pole) label offsets + anchor rules ----
    const bottomInfo = data
      .filter(d => !d.isDummy)
      .map(d => {
        const angle = (x(d.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
        const sinA = Math.sin(angle);
        const sortKey = x(d.key) + rotationOffset;
        return { key: d.key, sinA, sortKey };
      })
      .filter(r => r.sinA > 0.85)
      .sort((a, b) => a.sortKey - b.sortKey);

    const bottomOffsetMap = {};
    const bottomAnchorMap = {};
    const isBottomOdd = bottomInfo.length % 2 !== 0;
    const bottomMidIdx = Math.floor(bottomInfo.length / 2);

    bottomInfo.forEach((r, idx) => {
      const distFromEdge = Math.min(idx, bottomInfo.length - 1 - idx);
      bottomOffsetMap[r.key] = distFromEdge * blueLabelBottomVerticalGap;

      if (isBottomOdd && idx === bottomMidIdx) {
        bottomAnchorMap[r.key] = 'middle';
      } else if (idx < bottomInfo.length / 2) {
        bottomAnchorMap[r.key] = 'start';
      } else {
        bottomAnchorMap[r.key] = 'end';
      }
    });

    labelG.each(function(d, i) {
      const angle = (x(d.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
      const lineStart = innerRadius;

      const x1 = Math.cos(angle) * lineStart;
      const y1 = Math.sin(angle) * lineStart;
      const x2 = Math.cos(angle) * lineEnd;
      const y2 = Math.sin(angle) * lineEnd;

      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      const EPS = 0.2;
      let anchor = 'middle';
      if (cosA > EPS) anchor = 'start';
      else if (cosA < -EPS) anchor = 'end';
      if (Math.abs(cosA) <= EPS && sinA > 0) anchor = 'middle';

      const isTopPole = sinA < -0.85;
      const isBottomPole = sinA > 0.85;

      if (isTopPole) {
        const role = redRoleMap[d.key];
        if (role === 'leftmost') anchor = 'end';
        else if (role === 'rightmost') anchor = 'start';
      } else if (isBottomPole) {
        anchor = bottomAnchorMap[d.key] || anchor;
      }

      const redOffsetY = isTopPole ? (redOffsetMap[d.key] || 0) : 0;
      const bottomOffsetY = isBottomPole ? (bottomOffsetMap[d.key] || 0) : 0;
      const labelY = y2 + redOffsetY + bottomOffsetY;

      let x3 = x2;
      let horizontalLength;
      if (isTopPole) {
        horizontalLength = redLabelHorizontalLength;
      } else if (isBottomPole) {
        horizontalLength = blueLabelHorizontalLength;
      } else {
        horizontalLength = (i % 2 === 0) ? 20 : 10;
      }

      if (doHorizontal) {
        if (anchor === 'start') x3 = x2 + horizontalLength;
        else if (anchor === 'end') x3 = x2 - horizontalLength;
      }

      // 1) radial line
      d3.select(this).append('line')
        .attr('class', 'dashed-line-radial')
        .style('stroke', '#000')
        .style('stroke-dasharray', '4')
        .attr('x1', x1).attr('y1', y1)
        .attr('x2', x2).attr('y2', y2)
        .style('fill', 'none');

      // 2) small vertical connector
      if (redOffsetY !== 0 || bottomOffsetY !== 0) {
        d3.select(this).append('line')
          .attr('class', 'dashed-line-connector')
          .style('stroke', '#000')
          .style('stroke-dasharray', '4')
          .attr('x1', x2).attr('y1', y2)
          .attr('x2', x2).attr('y2', labelY)
          .style('fill', 'none');
      }

      // 3) horizontal line
      if (doHorizontal) {
        d3.select(this).append('line')
          .attr('class', 'dashed-line-horizontal')
          .style('stroke', '#000')
          .style('stroke-dasharray', '4')
          .attr('x1', x2).attr('y1', labelY)
          .attr('x2', x3).attr('y2', labelY)
          .style('fill', 'none');
      }

      let labelColor = '#454545';
      let percentColor = '#095BBD';
      // if (isTopPole) {
      //   labelColor = '#FF4D4F';
      //   percentColor = '#FF4D4F';
      // } else if (isBottomPole) {
      //   labelColor = '#095BBD';
      //   percentColor = '#095BBD';
      // }

      // 4) Text block
      if (doHorizontal) {
        d3.select(this).append('text')
          .attr('class', 'label-percent')
          .attr('fill', labelColor)
          .attr('x', x3)
          .attr('y', labelY)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .text(`${d.name} ${d.value}%`);
      } else {
        d3.select(this).append('text')
          .attr('class', 'label')
          .attr('fill', labelColor)
          .attr('x', x3)
          .attr('y', labelY + gap)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .text(d.name);

        d3.select(this).append('text')
          .attr('class', 'percent')
          .attr('fill', percentColor)
          .attr('x', x3)
          .attr('y', labelY + gap + 16)
          .attr('text-anchor', anchor)
          .attr('font-size', fontSize)
          .attr('font-weight', 600)
          .text(`${d.value}%`);
      }
    });

    // Grid value labels
    const dummy = data.find(d => d.isDummy);
    if (dummy) {
      const dummyAngle = (x(dummy.key) + x.bandwidth() / 2 + rotationOffset) - Math.PI / 2;
      g.selectAll('.grid-value-label')
        .data(gridValues)
        .enter()
        .append('text')
        .attr('class', 'grid-value-label')
        .attr('x', d => Math.cos(dummyAngle) * y(d))
        .attr('y', d => Math.sin(dummyAngle) * y(d) + 5)
        .attr('text-anchor', 'start')
        .attr('alignment-baseline', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#666')
        .text(d => d);
    }

    // center circle & text
    g.append('circle')
      .attr('r', 30)
      .attr('fill', '#e0e0e0');
    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', '20px')
      .attr('fill', '#999')
      .text(data.length - 1);

  }, [items, width, ht, redLabelTopVerticalGap, redLabelHorizontalLength, blueLabelBottomVerticalGap, blueLabelHorizontalLength, chartOffsetX, chartOffsetY]);

  return (
    <Wrapper className='graph' id={id}>
      <svg ref={ref} />
    </Wrapper>
  );
};

export default RadialBarChart;